import { useEffect, useMemo, useState } from "react";
import api from "../services/axiosInstance";
import { backendPort } from "../components/Fungsiku";
import { ShowPic } from "../components/ShowPic";

const toDateOnly = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const formatNum = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("id-ID");
};

const formatLokasi = (value, nopolByIdMt = new Map()) => {
  const raw = String(value || "").trim();
  if (!raw) return "-";
  if (/^K-[^#]*#.+$/i.test(raw)) {
    const [idMtRaw, posisiRaw] = raw.split("#");
    const idMt = String(idMtRaw || "").trim().toUpperCase();
    const posisi = String(posisiRaw || "").trim();
    const nopol = String(nopolByIdMt.get(idMt) || idMtRaw || "").trim();
    return `${nopol || "-"} | ${posisi || "-"}`;
  }
  const [head] = raw.split("#");
  return String(head || "").trim() || raw;
};

const moveUserToLast = (cols) => {
  const arr = Array.isArray(cols) ? cols : [];
  const withoutUser = arr.filter((col) => String(col || "").toLowerCase() !== "user");
  return arr.some((col) => String(col || "").toLowerCase() === "user")
    ? [...withoutUser, "user"]
    : withoutUser;
};

const parseLampiran = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (!raw) return [];
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

export function Page36_TabelHistoryBan({
  open,
  onClose,
  initialKeyword = "",
  opsiCabang = [],
  globalOpsi = {},
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [info, setInfo] = useState({ id_ban: "", seri_ban: "" });
  const [errorText, setErrorText] = useState("");
  const [lampiranFiles, setLampiranFiles] = useState([]);
  const [showLampiran, setShowLampiran] = useState(false);
  const nopolByIdMt = useMemo(
    () => new Map(
      (Array.isArray(globalOpsi?.opsiMT) ? globalOpsi.opsiMT : [])
        .map((x) => [String(x?.id_mt || "").trim().toUpperCase(), String(x?.nopol || "").trim()])
        .filter(([idMt, nopol]) => Boolean(idMt) && Boolean(nopol))
    ),
    [globalOpsi]
  );

  const fetchRows = async (rawKeyword) => {
    const cleaned = String(rawKeyword || "").trim();
    if (!cleaned) return;
    setIsLoading(true);
    setErrorText("");
    try {
      const { data } = await api.get(`${backendPort}/api/main/load_ban_history_event_modal`, {
        params: { keyword: cleaned },
        timeout: 120000,
      });
      const dataset = Array.isArray(data?.rows) ? data.rows : [];
      setRows(dataset);
      const firstRow = dataset.length > 0 ? dataset[0] : {};
      setInfo({
        id_ban: String(data?.id_ban || firstRow?.id_ban || ""),
        seri_ban: String(firstRow?.seri_ban || ""),
      });
    } catch (err) {
      setRows([]);
      setInfo({ id_ban: "", seri_ban: "" });
      setErrorText(err?.response?.data?.message || "Gagal memuat history Ban");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setShowLampiran(false);
      setLampiranFiles([]);
      return;
    }
    const first = String(initialKeyword || "").trim();
    if (!first) return;
    fetchRows(first);
  }, [open, initialKeyword]);

  const columns = useMemo(() => {
    const cabangMap = new Map(
      (Array.isArray(opsiCabang) ? opsiCabang : []).map((x) => [
        String(x?.kode_cabang || "").trim(),
        String(x?.nama_cabang || "").trim() || String(x?.kode_cabang || "").trim(),
      ])
    );
    const transformedRows = (Array.isArray(rows) ? rows : []).map((row) => {
      const next = { ...(row || {}) };
      const kode = String(next?.kode_cabang || "").trim();
      next.cabang = cabangMap.get(kode) || kode || "-";
      delete next.kode_cabang;
      delete next.id_event;
      delete next.id_ban;
      delete next.seri_ban;
      return next;
    });
    const set = new Set();
    transformedRows.forEach((row) => {
      Object.keys(row || {}).forEach((k) => set.add(k));
    });
    const priority = [
      "tgl",
      "event_ban",
      "cabang",
      "kmt_aktual",
      "td_aktual",
      "delta_tdmin",
      "status_ban",
      "place",
    ];
    const dynamic = Array.from(set);
    const head = priority.filter((x) => dynamic.includes(x));
    const tail = dynamic.filter((x) => !priority.includes(x));
    return { rows: transformedRows, cols: moveUserToLast([...head, ...tail]) };
  }, [rows, opsiCabang]);

  if (!open) return null;

  const getDisplayLabel = (col) => {
    const key = String(col || "").toLowerCase();
    if (key === "kmt_aktual") return "KMT BAN";
    if (key === "td_aktual") return "TD";
    if (key === "delta_tdmin") return "AUS";
    if (key === "status_ban") return "STATUS BAN";
    if (key === "place") return "LOKASI";
    if (key === "lampiran") return "LAMPIRAN";
    return String(col || "").replace(/_/g, " ").toUpperCase();
  };
  const isRightCol = (col) => ["kmt_aktual", "td_aktual", "delta_tdmin"].includes(String(col || "").toLowerCase());
  const isCenterCol = (col) => String(col || "").toLowerCase() === "status_ban";
  const getCellValue = (col, raw) => {
    const key = String(col || "").toLowerCase();
    if (key === "tgl") return toDateOnly(raw);
    if (key === "place") return formatLokasi(raw, nopolByIdMt);
    if (key === "kmt_aktual") return formatNum(raw);
    if (key === "delta_tdmin") return raw == null || raw === "" ? "-" : formatNum(raw);
    if (key === "td_aktual") return String(raw ?? "-");
    if (key === "lampiran") {
      const files = parseLampiran(raw);
      return files.length ? files.map((item) => String(item?.caption || item?.name || item?.file || "Dokumen")).join(" | ") : "-";
    }
    return String(raw ?? "-");
  };

  const downloadExcel = () => {
    const headers = ["no", ...columns.cols];
    const escapeHtml = (v) =>
      String(v ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\"/g, "&quot;");
    const headHtml = headers
      .map((h) => `<th style="background:#0f2d5c;color:#ffffff;font-weight:400;">${escapeHtml(String(h).replace(/_/g, " ").toUpperCase())}</th>`)
      .join("");
    const bodyHtml = columns.rows
      .map((row, idx) => {
        const rowCells = [
          `<td>${idx + 1}</td>`,
          ...columns.cols.map((col) => {
            const raw = row?.[col];
            const value = getCellValue(col, raw);
            const align = isRightCol(col) ? "text-align:right;padding-right:20px;" : "";
            return `<td style="${align}">${escapeHtml(value || "-")}</td>`;
          }),
        ];
        return `<tr>${rowCells.join("")}</tr>`;
      })
      .join("");
    const html = `\uFEFF<html><head><meta charset="UTF-8"></head><body><table border="1"><thead><tr>${headHtml}</tr></thead><tbody>${bodyHtml}</tbody></table></body></html>`;
    const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `history_event_ban_${String(info.id_ban || "unknown")}.xls`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="modal-backdrop-custom"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="modal-dialog-custom">
        <div className="modal-content-custom" style={{ width: "1100px", height: "80vh" }}>
          <div className="modal-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">History Event per Ban</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <div className="mb-2 d-flex justify-content-between align-items-start">
              <span style={{ fontWeight: 700, color: "#7a1f1f" }}>
                {`DATA HISTORY EVENT BAN ${info.id_ban || "-"} | Seri.${info.seri_ban || "-"}`}
              </span>
              <div className="d-flex align-items-center gap-2">
                {!!errorText && <small className="text-danger">{errorText}</small>}
                <button type="button" className="btn btn-success btn-sm align-self-start" onClick={downloadExcel}>
                  Download
                </button>
                <button type="button" className="btn btn-secondary btn-sm align-self-start" onClick={onClose}>
                  Close
                </button>
              </div>
            </div>

            <div className="border rounded" style={{ maxHeight: "61vh", overflow: "auto" }}>
              <table className="table table-sm mb-0">
                <thead style={{ position: "sticky", top: 0, background: "#0f2d5c", color: "#fff", zIndex: 2 }}>
                  <tr>
                    <th style={{ whiteSpace: "nowrap", minWidth: 60, background: "#0f2d5c", color: "#ffffff", fontWeight: 400 }}>No</th>
                    {columns.cols.map((col) => (
                      <th
                        key={col}
                        className={isCenterCol(col) ? "text-center" : (isRightCol(col) ? "text-end" : "")}
                        style={{ whiteSpace: "nowrap", minWidth: 140, background: "#0f2d5c", color: "#ffffff", fontWeight: 400, paddingRight: isRightCol(col) ? "20px" : undefined }}
                      >
                        {getDisplayLabel(col)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={Math.max(1, columns.cols.length + 1)} className="text-center text-muted">
                        {isLoading ? "Memuat data..." : "Tidak ada data."}
                      </td>
                    </tr>
                  )}
                  {columns.rows.map((row, idx) => (
                    <tr key={`row-${idx}`}>
                      <td>{idx + 1}</td>
                      {columns.cols.map((col) => {
                        const raw = row?.[col];
                        const value = getCellValue(col, raw);
                        const isLampiran = String(col || "").toLowerCase() === "lampiran";
                        const files = isLampiran ? parseLampiran(raw) : [];
                        return (
                          <td
                            key={`${idx}-${col}`}
                            className={isCenterCol(col) ? "text-center" : (isRightCol(col) ? "text-end" : "")}
                            style={{ whiteSpace: "nowrap", paddingRight: isRightCol(col) ? "20px" : undefined }}
                          >
                            {isLampiran && files.length > 0 ? (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => {
                                  setLampiranFiles(files.map((item, fileIndex) => {
                                    const filePath = String(item?.path || "").trim();
                                    return {
                                      url: filePath
                                        ? (filePath.startsWith("http") ? filePath : `${backendPort}${filePath}`)
                                        : "",
                                      name: String(item?.name || item?.file || `Dokumen ${fileIndex + 1}`),
                                      caption: String(item?.caption || item?.name || `Dokumen ${fileIndex + 1}`),
                                    };
                                  }).filter((item) => Boolean(item.url)));
                                  setShowLampiran(true);
                                }}
                              >
                                {`Lampiran (${files.length})`}
                              </button>
                            ) : (value || "-")}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <ShowPic
        open={showLampiran}
        onClose={() => setShowLampiran(false)}
        row={{}}
        backendPort={backendPort}
        token=""
        onUploadSuccess={() => {}}
        readOnly={true}
        customTitle="Lampiran Event Ban"
        customFiles={lampiranFiles}
        zIndex={1065}
      />
    </div>
  );
}

export default Page36_TabelHistoryBan;
